import { BlockError } from '@/blocks.04-project/block-error/block-error';
import toNumber from 'lodash-es/toNumber';
import { LazyInit } from '@/blocks.04-project/lazy-init/lazy-init';

/***
 * Класс загрузчика файлов
 * @example
 *
 * Аттрибуты:
 * data-max-size {number, default = Infinity} - максимальный размер файла в байтах
 * data-max-total-size {number, default = Infinity} - максимальный размер всех файлов в байтах
 * data-error-size {string} - текст ошибки при превышении размера файла
 * data-error-total-size {string} - текст ошибки при превышении общего размера всех файлов
 * data-error-extension {string, default} - текст ошибки если файл не правильного формата
 *
 * Классы:
 * .file-control_dragenter - вешается если пользователь держит файл над блоком
 * .file-control.validated - вешается если файл не прошёл валидацию
 */
class FileControl {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент с аттрибутом file-control
   */
  constructor(elem) {
		this.BlockError = new BlockError({ blockName: 'FileControl', destroy: this.destroy });

		if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.FileControl = this;
    this.block.AsyncFileControl = new Promise((resolve) => {
      this.promiseResolve = resolve;
    }).then((ins) => ins);

    this.LazyInit = new LazyInit({ ins: this, callback: this.init.bind(this), addClass: 'file-control_inited' });
    window.getBH((bh) => {
      this.bh = bh;
      this.LazyInit.init();
    });
  }

  init = () => {
    this.input = this.block.querySelector('.file-control__input');
    if (!this.input) this.BlockError.error('Отсутствует .file-control__input');

    this.fileList = this.block.querySelector('[data-file-list]');
    if (!this.fileList) this.BlockError.error('Отсутствует [data-file-list]');

    this.errorContainer = this.block.querySelector('[data-errors]');
    if (!this.errorContainer) this.BlockError.error('Отсутствует [data-errors]');

    this.maxSize = toNumber(this.block.dataset['maxSize']) || Infinity;
    this.maxTotalSize = toNumber(this.block.dataset['maxTotalSize']) || Infinity;
    this.isMultiple = this.input.multiple;
    this.currentSize = 0;

    this.DICTIONARY = (fileName) => ({
      size: `${this.block.dataset['errorSize'] || window.BX?.message('fileControlInvalidSize') || 'Превышен размер файла'}: ${fileName}`,
      totalSize: `${this.block.dataset['errorTotalSize'] || window.BX?.message('fileControlInvalidTotalSize') || 'Превышен общий размер всех файлов, файл не был добавлен'}: ${fileName}`,
      extension: `${this.block.dataset['errorExtension'] || window.BX?.message('fileControlInvalidExtension') || 'Не верный формат файла'}: ${fileName}`,
    });

    this.dt = new DataTransfer();

    this.form = this.block.closest('form');

    this.input.addEventListener('change', this.change);
    this.input.addEventListener('dragenter', this.addDrag);
    this.input.addEventListener('drop', this.removeDrag);
    this.input.addEventListener('dragleave', this.removeDrag);

   this.block.addEventListener('click', this.delete);

    if (this.form) this.form.addEventListener('reset', this.reset);

    this.change();

    this.promiseResolve(this);
    this.block.AsyncFileControl = this;
  };

  change = () => {
    if (!this.isMultiple) {
      this.dt = new DataTransfer();
      this.currentSize = 0;
      this.fileList.innerHTML = '';
    }

    this.block.classList.remove('invalid');
    this.errorContainer.innerHTML = '';

    for (let file of this.input.files) {
      if (!this.isAvailableExtension(file)) {
        this.block.classList.add('invalid');
        this.setInvalidFeedback(file?.name, 'extension');
        continue;
      } else if (!this.isAvailableSize(file)) {
        this.block.classList.add('invalid');
        this.setInvalidFeedback(file?.name, 'size');
        continue;
      }
      else if (this.currentSize + file.size > this.maxTotalSize) {
        this.block.classList.add('invalid');
        this.setInvalidFeedback(file?.name, 'totalSize');
        continue;
      }
      this.addItem(file);
      this.dt.items.add(file);
      this.currentSize += toNumber(file.size);
    }

    this.input.files = this.dt.files;
  };

  /***
   * Добавление нового файла в вёрстку
   * @param file{File} - файл
   */
  addItem = (file) => {
    this.fileList.insertAdjacentHTML('beforeend', this.bh.apply([
      {block: 'file-control', elem: 'file', cls: 'btn btn-secondary', attrs: {'data-file': true}, content: [
          {elem: 'icon-file', content: [
              {block: 'fi', mods: {icon: 'file'}}
            ]},
          {elem: 'info', content: [
              {elem: 'info-title', content: file.name},
              {elem: 'info-subtitle', content: `${this.getFileExtension(file)}, ${this.humanFileSize(file.size)}`},
            ]},
          {elem: 'delete', tag: 'button', attrs: {type: 'button', 'data-delete': true}, content: [
              {block: 'fi', mods: {icon: 'clear'}}
            ]},
        ]},
    ]));
  };

  /**
   * Установка сообщения об ошибке
   * @param name {String} имя файла
   * @param extension {'size'|'totalSize'|'extension'} - ключ с ошибкой
   */
  setInvalidFeedback = (name, extension) => {
    this.errorContainer.insertAdjacentHTML('beforeend', this.bh.apply([
      {cls: 'invalid-feedback', content: this.DICTIONARY(name)[extension]}
    ]));
  };

  /**
   * Проверка размера на доступность
   * @param file {File} - файл
   * @return {Boolean}
   */
  isAvailableSize = (file) => {
    return file.size <= this.maxSize;
  };

  /**
   * Проверка расширения на доступность
   * @param file {File} - файл
   * @return {Boolean}
   */
  isAvailableExtension = (file) => {
    const acceptArray = this.input.accept.split(',');
    const regValidate = acceptArray.some((extension) => {
      const regExtension = extension.replace('.', '\\\\.');
      return new RegExp(regExtension, 'i').test(file.type);
    });
    return regValidate || this.input.accept.includes(this.getFileExtension(file));
  };

  /**
   * Получение расширения файла
   * @param file {File} - файл
   * @return {string|null} - тип
   */
  getFileExtension = (file) => {
    const split = file.type.split('/');
    if (split.length >= 2) return split[1].toLowerCase();
    else return null;
  };

  /** Удаление файла */
  delete = (event) => {
    const target = event.target.closest('[data-delete]');
    if (!target) return;

    const file = target.closest('[data-file]');
    if (!file) return;

    const fileIndex = toNumber(Array.from(file.parentNode.children).indexOf(file));
    if (fileIndex === -1) return;

    file.remove();
    this.currentSize -= toNumber(this.dt.items[fileIndex]?.getAsFile()?.size) || 0;
    this.dt.items.remove(fileIndex);
    this.input.files = this.dt.files;
    this.block.classList.remove('invalid');
    this.errorContainer.innerHTML = '';
    this.input.dispatchEvent(new Event('iv.file-control.delete', {bubbles: true}));
  };

  /** Очистка инпута */
  reset = () => {
    this.input.value = '';
    this.currentSize = 0;
    this.fileList.innerHTML = '';
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Перевод байтов в другие размеры
   * @param bytes {Number} - число в байтах
   * @param float {Number} - чисел после запятой
   * @return {String} - конвертированная строка
   */
  humanFileSize = (bytes, float= 1) => {
    const i = bytes === 0 ? 0 : Math.floor(Math.log(bytes) / Math.log(1024));
    return toNumber((bytes / Math.pow(1024, i)).toFixed(float)) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
  };

  addDrag = () => this.block.classList.add('file-control_dragenter');

  removeDrag = () => this.block.classList.remove('file-control_dragenter');

  destroy = () => {
    if (this.form) this.form.addEventListener('reset', this.reset);
    if (this.input) {
      this.input.removeEventListener('change', this.change);
      this.input.removeEventListener('dragenter', this.addDrag);
      this.input.removeEventListener('drop', this.removeDrag);
      this.input.removeEventListener('dragleave', this.removeDrag);
    }
    if (this.block) {
      this.block.removeEventListener('click', this.delete);
      this.block.classList.remove('file-control_inited');
      delete this.block.FileControl;
      delete this.block.AsyncFileControl;
    }
  };

  static init() {
    Array.from(document.querySelectorAll('.file-control:not(.file-control_inited)')).forEach((elem) => new FileControl(elem));
  }
}

document.addEventListener('DOMContentLoaded', FileControl.init);

// Экспорт;
window.Block.FileControl = FileControl;
